import React from 'react'

import { FormattedMessage, useIntl } from 'react-intl'

import { PAGE_FILTER_VARIANTS } from '@components/filters/constants'
import { Popover } from '@components/ui'

import { getCheckboxListSelectedOptions, getPaidStatusFieldConfig } from '../helpers'
import type { FormPaidStatusFilterProps, PaidStatusFilterProps, WidgetPaidStatusFilterProps } from '../types'
import { CheckboxList } from './CheckboxList'

import { PaidStatusLabelMessage } from '@messages'
import { ButtonLabel, ButtonValue, MenuContainer, StyledButtonBase } from '../styles'

/**
 * Component to display paid status filter in PageFilterBar
 *
 * @param {WidgetPaidStatusFilterProps} {
 *  disabled (optional),
 *  fields,
 *  onChange,
 *  value
 * }
 */
function WidgetPaidStatusFilter({ disabled = false, fields, onChange, value }: WidgetPaidStatusFilterProps) {
  // display labels when they checked - only display first level label when all second level labels are checked
  const selectedOptions = getCheckboxListSelectedOptions(fields, value)

  return (
    <Popover
      placement="bottom-start"
      trigger={
        <StyledButtonBase disabled={disabled} data-testid="widget-paid-status-filter">
          <ButtonLabel>{PaidStatusLabelMessage}</ButtonLabel>
          <ButtonValue>
            {selectedOptions.join(', ') || (
              <FormattedMessage id="filterBar.searchFilter.searchAllPlaceholder" defaultMessage="Összes" />
            )}
          </ButtonValue>
        </StyledButtonBase>
      }
    >
      <MenuContainer>
        <CheckboxList fields={fields} onChange={onChange} value={value} variant="list" />
      </MenuContainer>
    </Popover>
  )
}

/**
 * Component to display paid status filter in FiltersDialog
 *
 * @param {FormPaidStatusFilterProps} {
 *  fields,
 *  onChange,
 *  value
 * }
 */
function FormPaidStatusFilter({ fields, onChange, value }: FormPaidStatusFilterProps) {
  return <CheckboxList fields={fields} onChange={onChange} value={value} variant="form" />
}

export default function PaidStatusFilter({
  disabled = false,
  onChange,
  value,
  variant = PAGE_FILTER_VARIANTS.widget,
}: PaidStatusFilterProps) {
  const { formatMessage } = useIntl()

  // create a render config with translated labels
  const fieldsConfigRef = React.useRef(getPaidStatusFieldConfig(formatMessage))

  if (variant === PAGE_FILTER_VARIANTS.widget) {
    return (
      <WidgetPaidStatusFilter disabled={disabled} fields={fieldsConfigRef.current} onChange={onChange} value={value} />
    )
  }

  return <FormPaidStatusFilter fields={fieldsConfigRef.current} onChange={onChange} value={value} />
}
